import React from 'react';
import {message} from 'antd';
import {CellViewMousePositionEventArgs} from '@antv/x6/src/view/cell/type';
import type {Dom} from '@antv/x6/src/common';
import type {IUseArchiX6EditorEffectsProps} from '@/types/hooks/diagramHooks.types';
import {IConnectionInvalidDropEvent, IConnectionSelectTypeEvent} from '@/types/graph/event.types';

/**
 * Побочные эффекты редактора: контекстное меню, выбор типа связи, Escape, события графа, переключение свойств в drawer.
 */
export function useArchiX6EditorEffects({
	modelerRef,
	modelerReady,
	viewReady,
	contextMenu,
	setContextMenu,
	connectionTypePicker,
	setConnectionTypePicker,
	setViewpointModalOpen,
	propertiesDrawerOpen,
	setPropertiesDrawerElement,
	readOnly = false,
}: IUseArchiX6EditorEffectsProps) {
	React.useEffect(() => {
		const modeler = modelerRef.current;
		if (!modeler || !viewReady) {
			return;
		}

		if (readOnly) {
			return;
		}
		const unsubscribe = modeler.setupContextMenu?.(setContextMenu);

		return () => unsubscribe?.();
	}, [viewReady, modelerRef, setContextMenu, readOnly]);

	React.useEffect(() => {
		if (!contextMenu) {
			return;
		}

		const close = (event: MouseEvent): void => {
			if (
				event.target instanceof Element &&
				event.target.closest?.(
					'.ant-dropdown, .ant-dropdown-menu-submenu, .ant-menu, .ant-modal-wrap'
				)
			) {
				return;
			}

			setContextMenu(null);
		};

		const mousedownTimeout = setTimeout(
			() => document.addEventListener('mousedown', close, true),
			0
		);

		return () => {
			clearTimeout(mousedownTimeout);
			document.removeEventListener('mousedown', close, true);
		};
	}, [contextMenu, setContextMenu]);

	React.useEffect(() => {
		if (!connectionTypePicker) {
			return;
		}

		const close = () => setConnectionTypePicker(null);

		const mousedownTimeout = setTimeout(
			() => document.addEventListener('mousedown', close, true),
			0
		);

		return () => {
			clearTimeout(mousedownTimeout);
			document.removeEventListener('mousedown', close, true);
		};
	}, [connectionTypePicker, setConnectionTypePicker]);

	React.useEffect(() => {
		const onEscape = (event: KeyboardEvent) => {
			if (event.key !== 'Escape') {
				return;
			}

			setConnectionTypePicker(null);
			setViewpointModalOpen(false);
			modelerRef.current?._connectionDragCleanup?.();
			modelerRef.current?._connectionModeCleanup?.();
		};

		document.addEventListener('keydown', onEscape);
		return () => document.removeEventListener('keydown', onEscape);
	}, [modelerRef, setConnectionTypePicker, setViewpointModalOpen]);

	React.useEffect(() => {
		const modeler = modelerRef.current;
		if (!modelerReady || !modeler) {
			return;
		}

		const graph = modeler.getGraph?.();
		if (!graph) return;
		const handler = ({nodeId, clientX, clientY}: IConnectionSelectTypeEvent) => {
			setConnectionTypePicker({
				nodeId,
				x: clientX ?? 0,
				y: clientY ?? 0,
			});
		};

		const onInvalidDrop = ({structuralBlock}: IConnectionInvalidDropEvent) => {
			if (structuralBlock) {
				void message.error('Нельзя создать связь между этими элементами.');
			} else {
				void message.error('Выбранный тип связи недопустим между этими элементами.');
			}
		};

		graph.on('connection:selectType', handler);
		graph.on('connection:invalidDrop', onInvalidDrop);

		return () => {
			graph.off('connection:selectType', handler);
			graph.off('connection:invalidDrop', onInvalidDrop);
		};
	}, [modelerReady, modelerRef, setConnectionTypePicker]);

	React.useEffect(() => {
		if (!viewReady || !propertiesDrawerOpen) {
			return;
		}
		const modeler = modelerRef.current;
		const graph = modeler?.getGraph?.();

		if (!graph) {
			return;
		}

		const onCellClick = ({cell}: CellViewMousePositionEventArgs<Dom.ClickEvent>) => {
			if (
				!cell?.id ||
				(typeof cell.isNode !== 'function' && typeof cell.isEdge !== 'function')
			) {
				return;
			}

			if (!cell.isNode?.() && !cell.isEdge?.()) {
				return;
			}

			const viewElement = modeler?._findViewElementById?.(cell.id);

			if (!viewElement) {
				return;
			}

			setPropertiesDrawerElement({
				businessObject: viewElement,
				id: viewElement?.id ?? viewElement?.$attrs?.id,
			});
		};

		graph.on('cell:click', onCellClick);

		return () => {
			graph.off('cell:click', onCellClick);
		};
	}, [viewReady, propertiesDrawerOpen, modelerRef, setPropertiesDrawerElement]);
}
